import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { t } from '@lingui/core/macro';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import Avatar from '@rover/components/src/Avatar';
import { Box, Button, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import { DataTypeOfZodSchema, Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import Alert from '@rover/kibble/patterns/Alert';
import Section from '@rover/kibble/patterns/Section';
import Dropzone from '@rover/react-lib/src/components/utils/Dropzone';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading';
import BackButtonWithFormState from '@rover/react-lib/src/pages/provider-profile/components/BackButtonWithFormState';
import { useApiCatProviderProfileCreate } from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-create';
import {
  getApiCatProviderProfileRetrieveQueryKey,
  useApiCatProviderProfileRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-retrieve';
import { useApiCatProviderProfileUpdate } from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-update';
import { CatProviderProfile } from '@rover/rsdk/src/apiClient/latest/model/catProviderProfile';
import { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import { HttpError } from '@rover/rsdk/src/errors';
import {
  MediaFile,
  MediaType,
  useSelectMedia,
  useUploadMedia,
} from '@rover/rsdk/src/modules/Media';
import { getWordCount } from '@rover/utilities/wordCount';

import { ReturnQueryParam } from '../types/ServiceForm';

import PetCareDetailsShimmer from './components/PetCareDetailsShimmer';
import useBackRouteParams from './hooks/useBackRouteParams';

const MAX_CHARACTER_COUNT = 35;
const MIN_WORD_COUNT = 25;

const getSubmitErrorMessage = (error: unknown): string => {
  const detail =
    error instanceof HttpError
      ? (error.rawResponse?.data as { detail?: string } | undefined)?.detail
      : undefined;
  return detail || t`Something went wrong saving your details. Please try again.`;
};

const schema = z.object({
  headline: z
    .string()
    .trim()
    .min(1, `This field is required.`)
    .max(MAX_CHARACTER_COUNT, `Please enter no more than ${MAX_CHARACTER_COUNT} characters.`),
  careExperience: z.string().refine((val) => (getWordCount(val) ?? 0) >= MIN_WORD_COUNT, {
    message: `Please write at least ${MIN_WORD_COUNT} words.`,
  }),
  yearsOfExperience: z.coerce
    .number()
    .int('Enter a whole number.')
    .min(1, 'You must have experience caring for pets to offer pet care through Rover.')
    .max(100),
  imageUrl: z.string().min(1, 'Profile photo is required.'),
});

type FormDataType = DataTypeOfZodSchema<typeof schema>;

const CatCareDetailsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { backRoute, onSuccessRedirect } = useBackRouteParams(ReturnQueryParam.CatExperienceAdded);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const { data: catProviderProfile, isLoading: catProviderProfileIsLoading } =
    useApiCatProviderProfileRetrieve();

  const { mutate: mutateCreate } = useApiCatProviderProfileCreate({
    mutation: {
      onMutate: () => {
        setSubmitError(undefined);
        setIsSubmitting(true);
      },
      onSuccess: async (response) => {
        if (response) {
          queryClient.setQueryData(getApiCatProviderProfileRetrieveQueryKey(), response);
        }
        setIsSubmitting(false);
        onSuccessRedirect();
      },
      onError: (error: unknown) => {
        setSubmitError(getSubmitErrorMessage(error));
        setIsSubmitting(false);
      },
    },
  });

  const { mutate: mutateUpdate } = useApiCatProviderProfileUpdate({
    mutation: {
      onMutate: () => {
        setSubmitError(undefined);
        setIsSubmitting(true);
      },
      onSuccess: async (response) => {
        queryClient.setQueryData(getApiCatProviderProfileRetrieveQueryKey(), response);
        setIsSubmitting(false);
        // Use window.location.assign for full page navigation
        // because the service page might be a Django template route
        // Add skipModal=true when returning from service form to prevent modal from showing again
        onSuccessRedirect();
      },
      onError: (error: unknown) => {
        setSubmitError(getSubmitErrorMessage(error));
        setIsSubmitting(false);
      },
    },
  });

  const { isUploading, uploadMedia } = useUploadMedia();

  // form default values
  const profilePhoto = catProviderProfile?.profilePhoto;
  const defaultValues = {
    headline: catProviderProfile?.headline ?? '',
    careExperience: catProviderProfile?.careExperience ?? '',
    yearsOfExperience: catProviderProfile?.yearsOfExperience ?? 0,
    imageUrl: profilePhoto?.isFallback === false ? profilePhoto.bigThumb ?? '' : '',
  };

  const {
    configureField,
    formProps,
    submitButtonProps,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useKibbleForm({
    schema,
    defaultValues,
    onValidSubmit: (values: FormDataType) => {
      // The image field holds a temporary `blob:` URL until the S3 upload
      // finalizes. Submitting that URL makes the backend reject the save with a
      // 400, so block until the finalized URL is in place. (The submit button is
      // also disabled while `isUploading`, but this guards the gap between the
      // upload settling and the button re-enabling.)
      if (values.imageUrl?.startsWith('blob:')) {
        setSubmitError(t`Please wait for your photo to finish uploading, then try again.`);
        return undefined;
      }

      const submissionData = {
        ...values,
      };

      if (catProviderProfile) {
        const data: CatProviderProfile = {
          ...submissionData,
          profilePhoto: catProviderProfile.profilePhoto,
        };

        if (values.imageUrl === catProviderProfile.profilePhoto?.bigThumb) {
          data.imageUrl = undefined;
        }
        return mutateUpdate({ data });
      }

      return mutateCreate({
        data: { profilePhoto: undefined as unknown as Image, ...submissionData },
      });
    },
    onInvalidSubmit: () => {},
  });

  // reset form when cat profile data loads
  React.useEffect(() => {
    if (catProviderProfile) {
      const photo = catProviderProfile.profilePhoto;
      reset({
        headline: catProviderProfile.headline ?? '',
        careExperience: catProviderProfile.careExperience ?? '',
        yearsOfExperience: catProviderProfile.yearsOfExperience ?? 0,
        imageUrl: photo?.isFallback === false ? photo.bigThumb ?? '' : '',
      });
    }
  }, [catProviderProfile, reset]);

  const { dropzoneProps } = useSelectMedia({
    multiple: false,
    type: MediaType.IMAGE,
  });

  const onImageSelect = (file: File): void => {
    const mediaFile = new MediaFile(file);

    // update form state to show the new image immediately
    setValue('imageUrl', mediaFile.uri, { shouldDirty: true, shouldValidate: true });
    setSubmitError(undefined);

    uploadMedia([{ file: mediaFile, opts: { objectType: 'cat-provider-profile' } }], {
      onUploadSuccess({ result }) {
        setValue('imageUrl', result.value.url, { shouldDirty: true, shouldValidate: true });
      },
      onUploadFailed() {
        // Clear the temporary `blob:` URL so the field no longer holds an
        // unusable value, and prompt the sitter to re-add the photo. Their
        // typed details stay intact, so a full reload isn't needed.
        setValue('imageUrl', '', { shouldDirty: true, shouldValidate: true });
        setSubmitError(t`Your photo didn't upload. Please add it again.`);
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{t`Cat experience`}</title>
      </Helmet>

      <Section maxWidth={['100%', '640px']} data-qa-id="pet-care-details-page" py="6x">
        <BackButtonWithFormState href={backRoute} skipModal={!isDirty} />

        <Box py="8x">
          <Heading size="400" mb="6x">
            {t`Cat experience`}
          </Heading>

          {submitError && (
            <Box mb="6x">
              <Alert role="alert" severity={Alert.Severity.DANGER}>{submitError}</Alert>
            </Box>
          )}

          {catProviderProfileIsLoading ? (
            <PetCareDetailsShimmer />
          ) : (
            <Form {...formProps} hideRequiredFieldWarning>
              <Flex alignItems="flex-start" marginBottom="10x" gap="4x" flexDirection="column">
                <Flex alignItems="flex-start" gap="4x" width="100%">
                  <Dropzone
                    {...dropzoneProps}
                    onDropAccepted={(files) => {
                      if (files[0]) {
                        onImageSelect(files[0]);
                      }
                    }}
                  >
                    <Box sx={{ cursor: 'pointer' }}>
                      <RoverLoading isLoading={isUploading}>
                        <Avatar
                          imageUrl={watch('imageUrl') ?? null}
                          diameter={64}
                          borderRadius="50%"
                          objectFit="cover"
                        />
                      </RoverLoading>
                    </Box>
                  </Dropzone>

                  <Flex flexDirection="column" flex="1" alignSelf="center">
                    <Text size="200" fontWeight="semibold">
                      {t`Photo`}
                    </Text>
                    <Paragraph size="100" textColor="tertiary" m="0x">
                      {t`Use a well-lit, clear photo of your face without sunglasses.`}
                    </Paragraph>
                  </Flex>
                </Flex>
                {errors.imageUrl && (
                  <Text size="100" textColor="error">
                    {errors.imageUrl.message as string}
                  </Text>
                )}
              </Flex>

              <TextField
                {...configureField('headline')}
                primaryLabel={t`Headline`}
                secondaryLabel={t`Make your headline short, descriptive and genuine.`}
                variantOptions={{
                  currentCharacterCount: errors?.headline ? undefined : watch('headline').length,
                }}
                required={false}
              />

              <TextField
                {...configureField('careExperience')}
                primaryLabel={t`Your experience`}
                secondaryLabel={t`What sets you apart from other sitters? Be sure to include any special skills like training young pets or senior care.`}
                placeholder={t`I've taken care of cats of every kind since I was 10. Neighbors, friends, and family have relied on me to check in with, feed and play with their pets...`}
                variant="textarea"
                variantOptions={{
                  minHeight: ['108px', '144px'],
                  minimumWordCount: MIN_WORD_COUNT,
                  currentWordCount: errors?.careExperience
                    ? undefined
                    : getWordCount(watch('careExperience') || '') || 0,
                }}
                required={false}
              />

              <TextField
                {...configureField('yearsOfExperience')}
                primaryLabel={t`Years of experience`}
                inputType="number"
                fieldWidth="100px"
                required
                hideRequiredAsterisk
              />

              <Flex
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                pt="6x"
                width="100%"
              >
                <Box flexGrow="1">
                  <Button
                    variant="primary"
                    fullWidth
                    {...submitButtonProps}
                    loading={isSubmitting}
                    disabled={submitButtonProps?.disabled || isUploading}
                  >
                    {t`Save & Continue`}
                  </Button>
                </Box>
              </Flex>
            </Form>
          )}
        </Box>
      </Section>
    </>
  );
};

export default CatCareDetailsPage;
